import { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  X,
  CheckCircle,
  AlertCircle,
  Loader2,
} from 'lucide-react';
import { useTestSession, type InputMode } from '@/contexts/useTestSession';
import type { FriendlyError } from '@/lib/errors';
import { track } from '@/lib/analytics';
import { useAudioPlayer } from '@/hooks/useAudioPlayer';
import ProgressBar from '@/components/interview/ProgressBar';
import QuestionDisplay from '@/components/interview/QuestionDisplay';
import InputModeToggle from '@/components/interview/InputModeToggle';
import VoiceInput from '@/components/interview/VoiceInput';
import TextInput from '@/components/interview/TextInput';
import ExitConfirmModal from '@/components/interview/ExitConfirmModal';
import Card from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import LoadingState from '@/components/ui/LoadingState';

const GRADING_STAGES = [
  'Reviewing your answers',
  'Matching USCIS scoring criteria',
  'Compiling your feedback',
  'Almost done',
];

const GRADING_TIPS = [
  'We score your answers the same way a real officer does.',
  'On the real USCIS exam, you answer 10 of 100 civics questions.',
  'Missed answers will become your personal practice queue.',
  'Every session builds your confidence for the real interview.',
];

export default function QuestionPage() {
  const navigate = useNavigate();
  const {
    status,
    currentQuestion,
    currentAnswer,
    currentIndex,
    totalQuestions,
    answeredCount,
    inputMode,
    isLastQuestion,
    isFirstQuestion,
    error: testError,
    submitAnswer,
    skipQuestion,
    nextQuestion,
    previousQuestion,
    setInputMode,
    completeTest,
    resetTest,
  } = useTestSession();

  const { playQuestion, isPlaying: isAudioPlaying, isLoading: isAudioLoading } =
    useAudioPlayer();

  const [showExitModal, setShowExitModal] = useState(false);
  const [displayError, setDisplayError] = useState<FriendlyError | null>(null);

  useEffect(() => {
    if (status === 'idle') {
      navigate('/interview/intro', { replace: true });
    }
  }, [status, navigate]);

  useEffect(() => {
    if (status === 'completed') {
      navigate('/interview/results', { replace: true });
    }
  }, [status, navigate]);

  const hasAnswer = currentAnswer !== null && !currentAnswer.skipped;

  const handlePlayAudio = useCallback(() => {
    if (currentQuestion) {
      playQuestion(currentQuestion.id);
    }
  }, [currentQuestion, playQuestion]);

  const lastPlayedIdRef = useRef<number | null>(null);
  useEffect(() => {
    if (!currentQuestion) return;
    if (lastPlayedIdRef.current === currentQuestion.id) return;
    lastPlayedIdRef.current = currentQuestion.id;
    handlePlayAudio();
  }, [currentQuestion?.id, currentQuestion, handlePlayAudio]);

  const handleVoiceTranscript = useCallback(
    (text: string) => {
      submitAnswer(text);
    },
    [submitAnswer]
  );

  const handleTextSubmit = useCallback(
    (text: string) => {
      submitAnswer(text);
    },
    [submitAnswer]
  );

  const handleInputError = useCallback((err: FriendlyError) => {
    setDisplayError(err);
  }, []);

  const handleNext = useCallback(() => {
    setDisplayError(null);
    if (isLastQuestion) {
      completeTest();
    } else {
      nextQuestion();
    }
  }, [isLastQuestion, completeTest, nextQuestion]);

  const handleSkip = useCallback(() => {
    setDisplayError(null);
    skipQuestion();
    if (!isLastQuestion) {
      nextQuestion();
    }
  }, [skipQuestion, nextQuestion, isLastQuestion]);

  const handlePrevious = useCallback(() => {
    setDisplayError(null);
    previousQuestion();
  }, [previousQuestion]);

  const handleModeChange = useCallback(
    (mode: InputMode) => {
      setInputMode(mode);
    },
    [setInputMode]
  );

  const handleExitConfirm = useCallback(() => {
    setShowExitModal(false);
    track('mock_test_abandoned', {
      questions_answered: answeredCount,
      total_questions: totalQuestions,
    });
    resetTest();
    navigate('/home', { replace: true });
  }, [resetTest, navigate, answeredCount, totalQuestions]);

  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-cream flex flex-col items-center justify-center px-6">
        <Loader2 size={48} className="text-primary animate-spin mb-4" />
        <p className="font-sans text-base text-text-secondary">Loading...</p>
      </div>
    );
  }

  if (status === 'submitting') {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center px-6">
        <LoadingState
          stages={GRADING_STAGES}
          tips={GRADING_TIPS}
          subtitle="Your results are on their way."
        />
      </div>
    );
  }

  if (!currentQuestion) return null;

  const questionKeywords = currentQuestion.question_text
    .split(' ')
    .filter((w) => w.length > 3);

  return (
    <div className="min-h-screen bg-cream flex flex-col">
      {/* Top bar */}
      <header className="border-b border-border/60 bg-card/40 backdrop-blur-sm">
        <div className="mx-auto w-full max-w-5xl px-6 md:px-8 lg:px-12 h-14 flex items-center justify-between">
          <span className="font-sans text-xs font-semibold uppercase tracking-[0.2em] text-text-muted">
            Mock Interview
          </span>
          <button
            onClick={() => setShowExitModal(true)}
            className="p-2 rounded-xl hover:bg-primary-light transition-colors cursor-pointer"
          >
            <X size={20} className="text-text-muted" />
          </button>
        </div>
      </header>

      {/* Progress */}
      <div className="border-b border-border/40">
        <div className="mx-auto w-full max-w-5xl px-6 md:px-8 lg:px-12 py-4">
          <ProgressBar
            current={currentIndex + 1}
            total={totalQuestions}
            answeredCount={answeredCount}
          />
        </div>
      </div>

      {/* Main: single centered column with reserved slots so layout never shifts */}
      <main className="flex-1 flex flex-col items-center justify-center px-6 py-8 gap-10 md:gap-12">
        {(displayError || testError) && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="w-full max-w-2xl"
          >
            <Card className="border-error/30 bg-error-light">
              <div className="flex items-start gap-3">
                <AlertCircle size={20} className="text-error flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-sans text-sm font-semibold text-error mb-1">
                    {(displayError || testError)?.title}
                  </p>
                  <p className="font-sans text-sm text-text-secondary">
                    {(displayError || testError)?.message}
                  </p>
                </div>
              </div>
            </Card>
          </motion.div>
        )}

        {/* Question slot — reserved min-height so 1-line and 3-line questions occupy the same space */}
        <div className="w-full max-w-2xl min-h-[13rem] md:min-h-[14rem] flex items-center justify-center">
          <QuestionDisplay
            questionText={currentQuestion.question_text}
            category={currentQuestion.category}
            onPlayAudio={handlePlayAudio}
            isAudioPlaying={isAudioPlaying}
            isAudioLoading={isAudioLoading}
          />
        </div>

        {/* Input slot — reserved min-height so voice↔text toggle doesn't shift surroundings */}
        <div className="w-full max-w-2xl min-h-[15rem] flex flex-col items-center justify-center gap-6">
          {!hasAnswer && (
            <>
              <div className="w-56">
                <InputModeToggle
                  mode={inputMode}
                  onModeChange={handleModeChange}
                  disabled={false}
                />
              </div>

              {inputMode === 'voice' ? (
                <VoiceInput
                  onTranscript={handleVoiceTranscript}
                  onError={handleInputError}
                  questionKeywords={questionKeywords}
                  disabled={false}
                />
              ) : (
                <div className="w-full">
                  <TextInput onSubmit={handleTextSubmit} disabled={false} />
                </div>
              )}
            </>
          )}

          {hasAnswer && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="w-full flex flex-col items-center gap-6"
            >
              <Card variant="compact" className="w-full border-success/30 bg-success-light">
                <div className="flex items-start gap-3">
                  <CheckCircle size={20} className="text-success flex-shrink-0 mt-0.5" />
                  <p className="font-sans text-sm text-text-primary text-left">
                    {currentAnswer?.userAnswer}
                  </p>
                </div>
              </Card>
              <Button onClick={handleNext}>
                {isLastQuestion ? 'Finish Test' : 'Next Question'}
              </Button>
            </motion.div>
          )}
        </div>
      </main>

      {/* Nav footer — always at viewport bottom, fixed min-h so main column sits consistently */}
      <div className="px-6 pb-6 pt-2 flex items-center justify-center gap-6 text-sm min-h-[40px]">
        {!isFirstQuestion && (
          <button
            onClick={handlePrevious}
            className="font-sans text-text-muted hover:text-text-secondary transition-colors cursor-pointer"
          >
            Previous
          </button>
        )}
        {!hasAnswer && !isFirstQuestion && (
          <span aria-hidden className="text-text-muted/40">·</span>
        )}
        {!hasAnswer && (
          <button
            onClick={handleSkip}
            className="font-sans text-text-muted hover:text-text-secondary transition-colors cursor-pointer"
          >
            Skip question
          </button>
        )}
      </div>

      <ExitConfirmModal
        visible={showExitModal}
        onCancel={() => setShowExitModal(false)}
        onConfirm={handleExitConfirm}
        answeredCount={answeredCount}
        totalQuestions={totalQuestions}
      />
    </div>
  );
}

